import { useMemo, useState } from "react";
import { Modal, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  ActionIconContainer,
  AddFormButton,
  CustomLoader,
  FilterButton,
} from "../../shared/components/TableElements";
import { FormItem, HintText } from "../../shared/components/form/FormElements";
import { formInputStyles } from "../../shared/constants/styles";
import { colorLightGray } from "../../utils/palette";
import { putApi } from "../../api/getUser";

const PAGE_SIZE = 1000;

const getDataType = (value) => {
  if (value === null || value === undefined) return "text";
  if (typeof value === "number") return "number";
  if (!isNaN(Date.parse(value))) return "date";
  return "text";
};

const getSortedFilteredData = (data, searchTerm, sortConfig) => {
  if (!Array.isArray(data)) return [];

  let filtered = searchTerm
    ? data.filter((row) =>
        Object.values(row).some((val) =>
          val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : data;

  if (sortConfig.key) {
    const type = getDataType(filtered[0]?.[sortConfig.key]);
    filtered = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (type === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (type === "date") {
        return sortConfig.direction === "asc"
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }

      return sortConfig.direction === "asc"
        ? aVal?.toString().localeCompare(bVal?.toString())
        : bVal?.toString().localeCompare(aVal?.toString());
    });
  }

  return filtered;
};

const TableModal = ({
  showModal,
  onClose,
  data,
  mode,
  onSaveClick,
  hasMore,
  sqlQuery,
}) => {
  const { t } = useTranslation("common");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [csvDownloading, setCsvDownloading] = useState(false);

  // const getSortedFilteredData = (data) => {
  //   let filtered = searchTerm
  //     ? data.filter((row) =>
  //         Object.values(row).some((val) =>
  //           val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  //         )
  //       )
  //     : data;

  //   if (sortConfig.key) {
  //     const type = getDataType(filtered[0]?.[sortConfig.key]);
  //     filtered = [...filtered].sort((a, b) => {
  //       const aVal = a[sortConfig.key];
  //       const bVal = b[sortConfig.key];

  //       if (type === "number") {
  //         return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
  //       }

  //       if (type === "date") {
  //         return sortConfig.direction === "asc"
  //           ? new Date(aVal) - new Date(bVal)
  //           : new Date(bVal) - new Date(aVal);
  //       }

  //       return sortConfig.direction === "asc"
  //         ? aVal?.toString().localeCompare(bVal?.toString())
  //         : bVal?.toString().localeCompare(aVal?.toString());
  //     });
  //   }

  //   return filtered;
  // };

  const sortedFilteredData = useMemo(() => {
    return getSortedFilteredData(data, searchTerm, sortConfig);
  }, [data, searchTerm, sortConfig]);
  // const paginatedData = sortedFilteredData.slice(
  //   (currentPage - 1) * PAGE_SIZE,
  //   currentPage * PAGE_SIZE
  // );

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderPagination = (dataLength) => {
    const totalPages = Math.ceil(dataLength / PAGE_SIZE);
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return (
      <Pagination className="justify-content-center mt-3">{pages}</Pagination>
    );
  };

  // const handleDownloadCSV = (table, index) => {
  //   const headers = Object.keys(table[0]).join(",");
  //   const rows = table.map((row) => Object.values(row).join(",")).join("\n");
  //   const csv = `${headers}\n${rows}`;
  //   const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  //   const link = document.createElement("a");
  //   link.href = URL.createObjectURL(blob);
  //   link.download = `report-${index + 1}.csv`;
  //   link.click();
  // };

  const escapeCSVValue = (val) => {
    if (val === null || val === undefined) return "";
    let str = val.toString();

    // Escape double quotes by replacing " with ""
    str = str.replace(/"/g, '""');

    // Wrap in double quotes if it contains comma, newlines, or double quotes
    if (str.includes(",") || str.includes("\n") || str.includes('"')) {
      str = `"${str}"`;
    }

    return str;
  };

  const downloadCSV = (data) => {
    if (!data.length) return;

    const headers = Object.keys(data[0]).map(escapeCSVValue).join(",");
    const rows = data
      .map((row) => Object.values(row).map(escapeCSVValue).join(","))
      .join("\n");

    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const locale = navigator.language || "en-US";
    const dateString = new Date().toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const safeDate = dateString
      .replace(/[,\s\/\.]+/g, "-")
      .replace(/[^\w\-]/g, "");
    const filename = `Simply Fleet Report ${safeDate}.csv`;

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handleCSVDownloadClick = async () => {
    setCsvDownloading(true);
    let data = null;

    if (hasMore) {
      try {
        let sanitized = sqlQuery;
        if (sanitized?.toLowerCase()?.includes("limit")) {
          sanitized = sqlQuery.replace(/\s+LIMIT\s+\d+;?\s*$/i, "").trim();
        }

        const apiData = {
          querystring: "get_report_data_from_query",
          sql_query: sanitized,
          limit: 0, //No limit
        };

        const res = await putApi(apiData, "dashboard");

        if (res?.success && Array.isArray(res.data)) {
          data = getSortedFilteredData(res.data, searchTerm, sortConfig);
        } else {
          console.error("CSV download failed or data is not an array", res);
          return;
        }
      } catch (e) {
        console.error("Error in handleCSVDownloadClick", e);
        return;
      }
    } else {
      data = sortedFilteredData;
    }

    if (data && data.length) {
      downloadCSV(data);
    }

    setCsvDownloading(false);
  };

  const nonSortableKeys = ["status"];

  return (
    <>
      <Modal show={showModal} onHide={onClose} size="xl" centered scrollable>
        <Modal.Header>
          <div className="d-flex flex-column">
            <h4>{t("full_report_count_records", { count: data?.length })}</h4>
            {hasMore && (
              <HintText>{`(${t("showing_first_1000_records_msg")})`}</HintText>
            )}
          </div>
          <div className="d-flex justify-content-end">
            <ActionIconContainer onClick={onClose}>
              <i className="fi fi-rr-cross-small" />
            </ActionIconContainer>
          </div>
        </Modal.Header>
        <Modal.Body className="m-0 p-0">
          <div>
            <div className="p-3 bg-white d-flex justify-content-between align-items-center">
              <div className="d-flex gap-2 align-items-center">
                <FormItem>
                  <input
                    type="text"
                    placeholder={t("filter_results")}
                    value={searchTerm}
                    style={{ ...formInputStyles, width: "16rem" }}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </FormItem>
                <span
                  style={{
                    background: colorLightGray,
                    //color: "#166534",
                    //border: `1px solid ${colorStatusActive}`,
                    borderRadius: "25px",
                    width: "min-content",
                    whiteSpace: "nowrap",
                    padding: "0.25rem 0.5rem",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {t("count_records", { count: sortedFilteredData?.length })}
                </span>
              </div>

              <div className="d-flex gap-3">
                {mode === "create-report" && (
                  <AddFormButton type="button" onClick={onSaveClick}>
                    {t("save_report")}
                  </AddFormButton>
                )}
                <FilterButton
                  type="button"
                  onClick={handleCSVDownloadClick}
                  disabled={csvDownloading}
                >
                  {csvDownloading ? <CustomLoader /> : t("download_csv")}
                </FilterButton>
              </div>
            </div>

            <div
              className="table-responsive mx-3 mb-3"
              style={{
                maxWidth: "100%",
                maxHeight: `calc(100vh - ${hasMore ? "230px" : "200px"})`,
                whiteSpace: "nowrap",
                border: `1px solid ${colorLightGray}`,
                borderRadius: "5px",
              }}
            >
              <Table size="md">
                <thead
                  //className="table-light"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    background: "white",
                  }}
                >
                  <tr>
                    {Object.keys(data[0] || {}).map((col, i) => (
                      <th
                        key={i}
                        style={{
                          cursor: nonSortableKeys.includes(col)
                            ? "default"
                            : "pointer",
                        }}
                        onClick={() =>
                          !nonSortableKeys.includes(col) && handleSort(col)
                        }
                      >
                        {col}{" "}
                        {sortConfig.key === col
                          ? sortConfig.direction === "asc"
                            ? "↑"
                            : "↓"
                          : ""}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {sortedFilteredData.length > 0 ? (
                    sortedFilteredData.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {Object.values(row).map((val, colIndex) => (
                          <td
                            key={colIndex}
                            style={{
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={val}
                          >
                            {val}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={Object.keys(data[0] || {}).length}
                        className="text-center py-2"
                      >
                        {t("no_records")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* {isModal && renderPagination(sortedFilteredData.length)} */}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TableModal;
